/* eslint simple-import-sort/imports: 0 */
/* eslint @typescript-eslint/no-non-null-assertion: 0 */

import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';

import SoundIcon from 'components/SoundIcon/SoundIcon';
import VideoIndicator from 'components/VideoIndicator/VideoIndicator';
import { VideoTypesCMS } from 'utils/sharedStrapiQueries/sharedTypes';

import { notifyPlayer } from './Video.utils';

import * as S from './Video.styles';

import useIsInView from 'hooks/useIsInView';

export type VideoProps = {
  video: {
    url: string;
  };
  errorMessage: string;
  videoType: VideoTypesCMS;
  src?: string;
  mediaSource?: VideoTypesCMS['source'];
  mediaSourceId?: string;
  azureAccountId?: string;
  azurePlaceholderSrc?: string;
  autoplayVideo?: boolean;
  playButton?: boolean;
  muteVideo?: boolean;
  soundButton?: boolean;
  vimeoBackground?: boolean;
  className?: string;
};

const Video: React.FunctionComponent<VideoProps> = ({
  video,
  errorMessage,
  videoType,
  src,
  mediaSource,
  mediaSourceId,
  azureAccountId,
  azurePlaceholderSrc,
  autoplayVideo,
  playButton,
  muteVideo,
  soundButton,
  vimeoBackground,
  className,
}) => {
  const props = {
    video: {
      url: video?.url || src || '',
    },
    videoType: {
      source: videoType?.source || mediaSource || 'self_hosted',
      sourceId: videoType?.sourceId || mediaSourceId || '',
      azureUserId: videoType?.azureUserId || azureAccountId || '',
      azurePlaceholder:
        videoType?.azurePlaceholder?.url || azurePlaceholderSrc || '',
      autoplay: videoType?.autoplay || autoplayVideo || false,
      manualPlay: videoType?.manualPlay || playButton || false,
      mute: videoType?.mute || muteVideo || false,
      withSound: videoType?.withSound || soundButton || false,
      vimeoBackground: videoType?.vimeoBackground || vimeoBackground || false,
    },
  };

  const [isMuted, setIsMuted] = useState<boolean>(props.videoType.mute);
  const [isPause, setIsPause] = useState<boolean>(!props.videoType.autoplay);
  const [isAzurePlaceholder, setIsAzurePlaceholder] = useState<boolean>(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { shouldPlay, targetRef } = useIsInView(0.35, 0.5);
  const router = useRouter();

  useEffect(() => {
    // In view autoplay functionality
    if (videoType.source === 'self_hosted') {
      if (videoType.autoplay)
        shouldPlay ? videoRef?.current!.play() : videoRef?.current!.pause();
    }

    if (videoType.source === 'azure')
      notifyPlayer(iframeRef, () => null, shouldPlay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPlay]);

  useEffect(() => {
    if (iframeRef.current && props.videoType.source === 'vimeo') {
      import('@vimeo/player').then(({ default: Player }) => {
        const player = new Player(iframeRef.current);

        player.setMuted(isMuted);

        const playPause = async () => {
          try {
            shouldPlay ? await player.play() : await player.pause();
          } catch (e) {}
        };

        // In view autoplay functionality
        if (videoType.autoplay) playPause();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeRef, isMuted, shouldPlay]);

  const onHandlePlay = () => {
    setIsPause(false);
    videoRef.current!.play();
    videoRef.current!.controls = false;
  };

  const onHandlePause = () => {
    setIsPause(true);
    videoRef.current!.pause();
  };

  const selfHostedContent = () => (
    <S.SelfHostedWrapper ref={targetRef}>
      <S.Video
        ref={videoRef}
        onClick={isPause ? onHandlePlay : onHandlePause}
        muted={isMuted}
        loop
        autoPlay={props.videoType.autoplay}
        preload="auto"
        playsInline
      >
        <source src={props.video.url} type="video/mp4" />
        {errorMessage}
      </S.Video>

      <S.IndicatorsWrapper>
        {props.videoType.manualPlay && (
          <VideoIndicator
            isPause={isPause}
            onPlay={onHandlePlay}
            onPause={onHandlePause}
            size={0.35}
          />
        )}
        {props.videoType.withSound && (
          <SoundIcon isMuted={isMuted} onClick={() => setIsMuted(!isMuted)} />
        )}
      </S.IndicatorsWrapper>
    </S.SelfHostedWrapper>
  );

  const embeddedContent = () => {
    const vimeoUrl = `https://player.vimeo.com/video/${
      props.videoType.sourceId
    }?autoplay=${props.videoType.autoplay}&loop=1&muted=${
      props.videoType.mute
    }&autopause=0&controls=${!props.videoType.autoplay}&background=${
      props.videoType.vimeoBackground
    }`;
    const azureUrl = `https://www.videoindexer.ai/embed/player/${
      props.videoType.azureUserId
    }/${props.videoType.sourceId}/?locale=${router.locale}&autoplay=${
      (props.videoType.azurePlaceholder && !props.videoType.autoplay) ||
      props.videoType.autoplay
    }`;

    const renderCondition = () => {
      if (props.videoType.sourceId) {
        if (props.videoType.source === 'vimeo') return true;
        else if (
          props.videoType.source === 'azure' &&
          props.videoType.azureUserId
        )
          return true;
        else return false;
      } else return false;
    };
    const placeholderCondition =
      props.videoType.source === 'azure' &&
      props.videoType.azurePlaceholder.length > 1 &&
      isAzurePlaceholder &&
      !props.videoType.autoplay;

    return (
      <S.IframeWrapper
        ref={targetRef}
        onClick={() =>
          placeholderCondition && notifyPlayer(iframeRef, setIsAzurePlaceholder)
        }
      >
        {placeholderCondition && (
          <S.AzurePlaceholder
            imageSrc={props.videoType.azurePlaceholder || ''}
          />
        )}
        {renderCondition() && (
          <S.Iframe
            ref={iframeRef}
            src={props.videoType.source === 'vimeo' ? vimeoUrl : azureUrl}
            frameBorder="0"
            allowFullScreen
            allow="autoplay; fullscreen; encrypted-media"
          />
        )}
        {props.videoType.source !== 'azure' && props.videoType.withSound && (
          <S.IconIndicatorWrapper isVimeo={props.videoType.source === 'vimeo'}>
            <SoundIcon isMuted={isMuted} onClick={() => setIsMuted(!isMuted)} />
          </S.IconIndicatorWrapper>
        )}
      </S.IframeWrapper>
    );
  };

  return (
    <S.VideoContainer className={className}>
      {props.videoType.source === 'self_hosted'
        ? selfHostedContent()
        : embeddedContent()}
    </S.VideoContainer>
  );
};

export default Video;
